import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaHome, FaLongArrowAltLeft, FaPowerOff } from 'react-icons/fa';

// Phone remote for the TV when nothing is playing. It follows the JioSphere
// "remote card" pattern: air-mouse/gyroscope controls (optional), horizontal
// scroll toggle, speed slider, Back/Home/OK, a large trackpad, and a vertical
// scroll rail. Commands reuse the MQTT pointer channel; the TV app translates
// them to pointer/wheel events.

const TAP_DISTANCE = 14;
const TAP_MAX_MS = 300;
const MOVE_INTERVAL_MS = 30;
const MOVE_MIN_DELTA = 0.002;
const SCROLL_INTERVAL_MS = 45;
const MAX_WHEEL_DELTA = 220;

const clamp01 = (value) => Math.min(1, Math.max(0, Number(value) || 0));
const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, Number(value) || 0));
const fmtSpeed = (n) => (Math.round(n * 10) / 10).toFixed(1);

const JioRemote = ({ code, statusLine, onCommand, onDisconnect, onSwitch, switchLabel }) => {
  const trackRef = useRef(null);
  const railRef = useRef(null);
  const gestureRef = useRef(null);
  const railGestureRef = useRef(null);
  const posRef = useRef({ x: 0.5, y: 0.5 });
  const lastMoveSent = useRef(0);
  const lastMovePos = useRef({ x: 0.5, y: 0.5 });
  const lastScrollSent = useRef(0);

  const [airMouse, setAirMouse] = useState(false);
  const [horizontalScroll, setHorizontalScroll] = useState(false);
  const [speed, setSpeed] = useState(2.5);
  const [cursor, setCursor] = useState({ x: 50, y: 50, visible: false });
  const hideTimer = useRef(null);

  const hideCursor = useCallback(() => {
    setCursor((c) => ({ ...c, visible: false }));
  }, []);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  const showCursorAt = useCallback((x, y) => {
    setCursor({ x: x * 100, y: y * 100, visible: true });
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(hideCursor, 1200);
  }, [hideCursor]);

  const sendMove = useCallback((x, y) => {
    const now = Date.now();
    if (now - lastMoveSent.current < MOVE_INTERVAL_MS) return;
    const prev = lastMovePos.current;
    if (Math.abs(x - prev.x) < MOVE_MIN_DELTA && Math.abs(y - prev.y) < MOVE_MIN_DELTA) return;
    lastMoveSent.current = now;
    lastMovePos.current = { x, y };
    onCommand({ type: 'pointer', phase: 'move', x, y });
  }, [onCommand]);

  const sendWheel = useCallback((deltaX, deltaY) => {
    const now = Date.now();
    if (now - lastScrollSent.current < SCROLL_INTERVAL_MS) return;
    const dx = Number(deltaX) || 0;
    const dy = Number(deltaY) || 0;
    if (!dx && !dy) return;
    lastScrollSent.current = now;

    const scaledX = horizontalScroll ? dx * speed : 0;
    const scaledY = horizontalScroll ? 0 : dy * speed;

    onCommand({
      type: 'pointer',
      phase: 'wheel',
      x: posRef.current.x,
      y: posRef.current.y,
      deltaX: clamp(scaledX, -MAX_WHEEL_DELTA, MAX_WHEEL_DELTA),
      deltaY: clamp(scaledY, -MAX_WHEEL_DELTA, MAX_WHEEL_DELTA),
    });
  }, [horizontalScroll, onCommand, speed]);

  const onTrackDown = (e) => {
    const pad = trackRef.current;
    if (!pad) return;
    try { pad.setPointerCapture(e.pointerId); } catch (err) { /* unsupported */ }
    // Keep the TV cursor where it is. The pad is a relative trackpad: dragging
    // moves the cursor, it doesn't jump to the tapped point.
    gestureRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      startedAt: Date.now(),
    };
    showCursorAt(posRef.current.x, posRef.current.y);
  };

  const onTrackMove = (e) => {
    const g = gestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const pad = trackRef.current;
    if (!pad) return;
    const rect = pad.getBoundingClientRect();
    // Multiply movement by speed so the same drag can cover more screen space
    // (speed doubles as pointer sensitivity).
    const dx = ((e.clientX - g.lastX) / rect.width) * speed;
    const dy = ((e.clientY - g.lastY) / rect.height) * speed;
    const prev = posRef.current;
    const next = {
      x: clamp01(prev.x + dx),
      y: clamp01(prev.y + dy),
    };
    posRef.current = next;
    showCursorAt(next.x, next.y);
    sendMove(next.x, next.y);
    g.lastX = e.clientX;
    g.lastY = e.clientY;
  };

  const onTrackUp = (e) => {
    const g = gestureRef.current;
    const pad = trackRef.current;
    if (pad) {
      try { pad.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
    }
    if (!g || g.pointerId !== e.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const elapsed = Date.now() - g.startedAt;
    if (distance <= TAP_DISTANCE && elapsed <= TAP_MAX_MS) {
      onCommand({ type: 'pointer', phase: 'click', x: posRef.current.x, y: posRef.current.y });
    }
    gestureRef.current = null;
  };

  const onTrackCancel = () => { gestureRef.current = null; };

  const onRailDown = (e) => {
    const rail = railRef.current;
    if (!rail) return;
    try { rail.setPointerCapture(e.pointerId); } catch (err) { /* unsupported */ }
    railGestureRef.current = {
      pointerId: e.pointerId,
      lastY: e.clientY,
    };
  };

  const onRailMove = (e) => {
    const g = railGestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dy = e.clientY - g.lastY;
    if (dy !== 0) sendWheel(0, -dy * 1.6);
    g.lastY = e.clientY;
  };

  const onRailUp = (e) => {
    const rail = railRef.current;
    if (rail) {
      try { rail.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
    }
    railGestureRef.current = null;
  };

  return (
    <div className="min-h-screen pt-20 pb-24 px-3 select-none bg-[#111318] text-white">
      <div className="max-w-md mx-auto flex flex-col gap-3">

        {/* Air mouse (optional, disabled when unsupported) */}
        <div className="flex items-center justify-between bg-[#f4f4f6] text-black rounded-2xl px-5 py-4 shadow">
          <div>
            <p className="text-[17px] font-semibold">Use Air Mouse (Gyroscope)</p>
            <p className="text-[10px] text-gray-500">Unsupported on this phone</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={airMouse}
            aria-label="Toggle air mouse"
            onClick={() => setAirMouse(false)}
            className={`w-12 h-7 rounded-full p-1 transition-colors ${airMouse ? 'bg-blue-600' : 'bg-black/70'}`}
          >
            <span className={`block w-5 h-5 bg-gray-200 rounded-full shadow transition-transform ${airMouse ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        {/* Horizontal scroll toggle */}
        <div className="flex items-center justify-between bg-[#f4f4f6] text-black rounded-2xl px-5 py-4 shadow">
          <p className="text-[17px] font-semibold">Horizontal Scroll</p>
          <button
            type="button"
            role="switch"
            aria-checked={horizontalScroll}
            aria-label="Toggle horizontal scroll"
            onClick={() => setHorizontalScroll((v) => !v)}
            className={`w-12 h-7 rounded-full p-1 transition-colors ${horizontalScroll ? 'bg-black' : 'bg-black/70'}`}
          >
            <span className={`block w-5 h-5 bg-gray-200 rounded-full shadow transition-transform ${horizontalScroll ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        {/* Speed slider */}
        <div className="flex items-center gap-3 bg-[#f4f4f6] text-black rounded-2xl px-5 py-4 shadow">
          <span className="text-[15px] font-semibold">Speed</span>
          <input
            type="range"
            min="0.5"
            max="4"
            step="0.5"
            value={speed}
            aria-label="Scroll speed"
            onChange={(e) => setSpeed(Number(e.target.value) || 2.5)}
            className="flex-1 accent-black"
          />
          <span className="text-[15px] font-bold w-10 text-right">{fmtSpeed(speed)}x</span>
        </div>

        {/* Back / Home / OK */}
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => onCommand({ type: 'nav', value: 'back' })}
            aria-label="Back"
            className="bg-[#f4f4f6] text-[#2b3bc8] rounded-2xl py-4 shadow active:scale-95 transition-all flex items-center justify-center"
          >
            <FaLongArrowAltLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => onCommand({ type: 'nav', value: 'home' })}
            aria-label="Home"
            className="bg-[#f4f4f6] text-[#2b3bc8] rounded-2xl py-4 shadow active:scale-95 transition-all flex items-center justify-center"
          >
            <FaHome className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => onCommand({ type: 'pointer', phase: 'click', x: posRef.current.x, y: posRef.current.y })}
            aria-label="OK"
            className="bg-[#f4f4f6] text-[#2b3bc8] rounded-2xl py-4 shadow active:scale-95 transition-all flex items-center justify-center text-lg font-black"
          >
            OK
          </button>
        </div>

        {/* Trackpad card + vertical scroll rail */}
        <div className="relative h-[46vh] min-h-[280px] max-h-[460px] flex rounded-2xl overflow-hidden shadow border border-white/10">
          <div
            ref={trackRef}
            role="application"
            aria-label="TV trackpad"
            onPointerDown={onTrackDown}
            onPointerMove={onTrackMove}
            onPointerUp={onTrackUp}
            onPointerCancel={onTrackCancel}
            className="relative flex-1 bg-gradient-to-br from-[#b7b7e6] via-[#a9a9de] to-[#8e8ed1] flex items-center justify-center"
            style={{ touchAction: 'none' }}
          >
            <svg width="62" height="62" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="7" y="2" width="10" height="20" rx="5" stroke="rgba(255,255,255,.78)" strokeWidth="1.6" />
              <path d="M12 6v4" stroke="rgba(255,255,255,.78)" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <div
              aria-hidden="true"
              className={`absolute pointer-events-none transition-opacity duration-200 ${cursor.visible ? 'opacity-100' : 'opacity-0'}`}
              style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}
            >
              <div className="w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white bg-black/40 shadow-[0_0_12px_rgba(255,255,255,.6)]" />
            </div>
            <p className="absolute bottom-3 text-[10px] text-white/80 font-semibold">Drag to move cursor · tap to click</p>
          </div>

          {/* Scroll rail */}
          <div
            ref={railRef}
            aria-label="TV scroll rail"
            onPointerDown={onRailDown}
            onPointerMove={onRailMove}
            onPointerUp={onRailUp}
            onPointerCancel={onRailUp}
            className="w-16 bg-[#c7c7dc] border-l border-black/10 relative"
            style={{ touchAction: 'none' }}
          >
            <button
              type="button"
              onClick={() => sendWheel(0, -80)}
              aria-label="Scroll up"
              className="absolute top-4 left-0 right-0 flex items-center justify-center text-gray-600"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="absolute top-16 bottom-16 left-1/2 -translate-x-1/2 w-0.5 bg-gray-500/50 rounded" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-10 bg-[#8e8ed1] rounded-full shadow" />
            <button
              type="button"
              onClick={() => sendWheel(0, 80)}
              aria-label="Scroll down"
              className="absolute bottom-4 left-0 right-0 flex items-center justify-center text-gray-600"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>

        {/* Bottom action strip */}
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => onCommand({ type: 'nav', value: 'back' })}
            className="bg-[#f4f4f6]/15 text-gray-300 border border-white/10 rounded-2xl py-3 text-[10px] font-bold"
          >
            BACK
          </button>
          <button
            type="button"
            onClick={() => onCommand({ type: 'pointer', phase: 'click', x: posRef.current.x, y: posRef.current.y })}
            className="bg-[#f4f4f6]/15 text-gray-300 border border-white/10 rounded-2xl py-3 text-[10px] font-bold"
          >
            OK
          </button>
        <div className="flex items-center gap-2">
          {onSwitch && (
            <button
              type="button"
              onClick={onSwitch}
              className="text-[10px] font-bold bg-blue-600 text-white px-3 py-2 rounded-xl active:scale-95 transition-all"
            >
              {switchLabel || 'Switch'}
            </button>
          )}
          <button
            type="button"
            onClick={onDisconnect}
            aria-label="Disconnect from TV"
            className="p-2.5 rounded-full bg-white/5 text-gray-300 border border-white/10 hover:bg-white/15 active:scale-95 transition-all"
          >
            <FaPowerOff className="w-4 h-4" />
          </button>
        </div>
        </div>

        {statusLine && <p className="text-center text-[10px] text-gray-500">{statusLine}</p>}
        <p className="text-center text-[10px] text-gray-600 font-mono">CODE {String(code || '').padEnd(6, '-')}</p>
      </div>
    </div>
  );
};

export default JioRemote;
